import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { fetchEmailTemplates, type EmailLibraryTemplate } from "src/services/CampaignAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import {
  DEFAULT_BLANK_TEMPLATE_ID,
  TEMPLATE_EDITOR_KEY,
  TEMPLATES_PAGE_SIZE,
} from "../TemplateEditorConstants";

const SEARCHABLE_FIELDS: (keyof EmailLibraryTemplate)[] = [
  "name",
  "badge",
  "description",
  "campaign_subtype",
];

export const useEmailTemplates = () => {
  const { i18n } = useTranslation();
  const [templates, setTemplates] = useState<EmailLibraryTemplate[]>([]);
  const [blankTemplateId, setBlankTemplateId] = useState(DEFAULT_BLANK_TEMPLATE_ID);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetchEmailTemplates(getApiLangFromI18n(i18n));
      setTemplates(Array.isArray(response?.templates) ? response.templates : []);
      setBlankTemplateId(response?.blank_template_id || DEFAULT_BLANK_TEMPLATE_ID);
    } catch (error) {
      message.error(
        getThrownApiDetail(error) || i18n.t(`${TEMPLATE_EDITOR_KEY}.errors.loadTemplatesFailed`)
      );
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return templates;
    }
    return templates.filter(template =>
      SEARCHABLE_FIELDS.some(field => template[field]?.toLowerCase().includes(term))
    );
  }, [searchTerm, templates]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / TEMPLATES_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageTemplates = useMemo(() => {
    const start = (safePage - 1) * TEMPLATES_PAGE_SIZE;
    return filteredTemplates.slice(start, start + TEMPLATES_PAGE_SIZE);
  }, [filteredTemplates, safePage]);

  const updateSearchTerm = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);
  const goToPreviousPage = useCallback(() => setCurrentPage(Math.max(1, safePage - 1)), [safePage]);
  const goToNextPage = useCallback(
    () => setCurrentPage(Math.min(totalPages, safePage + 1)),
    [safePage, totalPages]
  );

  return {
    templates: pageTemplates,
    currentPage: safePage,
    totalPages,
    goToPreviousPage,
    goToNextPage,
    hasTemplates: templates.length > 0,
    blankTemplateId,
    isLoading,
    hasError,
    searchTerm,
    setSearchTerm: updateSearchTerm,
    loadTemplates,
  };
};

export type EmailTemplatesState = ReturnType<typeof useEmailTemplates>;
